import React, { useEffect } from 'react';
import styled from 'styled-components';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { IntlShape, injectIntl } from 'react-intl';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import TablePagination from '@material-ui/core/TablePagination';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import { translate } from 'containers/services/translate';
import messages from 'containers/PreOpenManagerRejectedClaims/messages';
import { ROW_PER_PAGE } from 'constants/global';
import { pushTo } from 'containers/services/routes-helper';
import { PAGES } from 'routes';
import { requestRejectedClaims } from 'containers/PreOpenManagerRejectedClaims/actions';
import {
  makeSelectRejectedClaims,
  makeSelectCountRejectedClaims,
} from 'containers/PreOpenManagerRejectedClaims/selectors';
import { CLAIM_STATUS } from 'containers/data-structure';
import { ClaimByStatus, Listings } from 'containers/AppContainer/typings';
import { IconButton } from '@material-ui/core';
import ErrorOutlineRoundedIcon from '@material-ui/icons/ErrorOutlineRounded';
import { FEATURES } from 'features/features';
import * as featureService from 'features/fflip-helpers';
import RejectedReasonsDrawer from 'containers/PreOpenManagerRejectedClaims/RejectedReasonsDrawer';
import { get } from 'lodash';
import { makeSelectListings } from 'containers/AppContainer/selectors';
import CardTitle from 'containers/common/Card/CardTitle';

export interface PreOpenManagerRejectedClaimsProps {
  intl: IntlShape;
  requestRejectedClaims: Function;
  rejectedClaims: ClaimByStatus[];
  countClaims: number;
  listings: Listings;
}

const Container = styled.div`
   {
    margin-top: 50px;
    padding-top: 0px;
  }
  .title {
    text-align: left;
    font-family: Roboto;
    font-weight: Bold;
    font-size: 18px;
    letter-spacing: 0;
    color: #384264;
    opacity: 1;
    padding: 10px;
  }
  .headerTitle {
    text-align: left;
    font-size: 11px;
    letter-spacing: 0;
    color: #000000de;
    text-transform: none;
    opacity: 1;
    font-family: Roboto;
    padding-bottom: 0px;
    font-weight: Bold;
  }
  .row {
    border-radius: 4px;
    text-align: left;
    font-family: Roboto;
    letter-spacing: 0;
    font-size: 11px;
    color: #2b2727;
  }
  .claims {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
  }
  .icon-rejection {
    color: #f72b2b;
  }
  .rejection-reasons {
    display: flex;
  }
`;

export const PreOpenManagerRejectedClaims: React.FC<PreOpenManagerRejectedClaimsProps> = (
  props,
): JSX.Element => {
  const [page, setPage] = React.useState(0);
  const [pageSize, setRowsPerPage] = React.useState(25);
  const [isOpen, setIsOpen] = React.useState(false);
  const [index, setIndex] = React.useState(0);

  const handleOpenDrawer = (index: number) => (event: {
    stopPropagation: Function;
  }) => {
    event.stopPropagation();
    setIsOpen(true);
    setIndex(index);
  };

  const handleCloseDrawer = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    props.requestRejectedClaims(CLAIM_STATUS.REJECTED, page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* istanbul ignore next */
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    props.requestRejectedClaims(CLAIM_STATUS.REJECTED, newPage, pageSize);
  };

  /* istanbul ignore next */
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const size = parseInt(event.target.value, 10);
    setRowsPerPage(size);
    props.requestRejectedClaims(CLAIM_STATUS.REJECTED, page, size);
  };

  return (
    <Container>
      <Card>
        <CardContent>
          <CardTitle
            count={props.countClaims}
            message={translate({ ...messages.title }, props)}
          />
          <div>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.claimNumber }, props)}
                  </TableCell>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.registrationNumber }, props)}
                  </TableCell>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.openingDate }, props)}
                  </TableCell>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.lossDate }, props)}
                  </TableCell>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.claimPlace }, props)}
                  </TableCell>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.lossHour }, props)}
                  </TableCell>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.numberOfVictim }, props)}
                  </TableCell>
                  {featureService.hasFeature(FEATURES.LAWYER_INDEXATION) && (
                    <TableCell />
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {/* istanbul ignore next */
                props.rejectedClaims.map((row, _index) => (
                  <TableRow
                    key={row.id}
                    onClick={pushTo(
                      `${PAGES.PREOPEN_MANAGER_RECAP_PAGE}/${row.id}`,
                    )}
                  >
                    <TableCell align="left" className="row">
                      {row.claimNumber}
                    </TableCell>
                    <TableCell align="left" className="row">
                      {row.registrationNumber}
                    </TableCell>
                    <TableCell align="left" className="row">
                      {moment(row.createdAt).format('YYYY-MM-DD')}
                    </TableCell>
                    <TableCell align="left" className="row">
                      {moment(row.lossDate).format('YYYY-MM-DD')}
                    </TableCell>
                    <TableCell align="left" className="row">
                      {row.lossLocation}
                    </TableCell>
                    <TableCell align="left" className="row">
                      {row.lossHour}
                    </TableCell>
                    <TableCell align="left" className="row">
                      {row.victims.length}
                    </TableCell>
                    {featureService.hasFeature(
                      FEATURES.CLAIM_REJECTION_REASONS,
                    ) && (
                      <TableCell align="left" className="row">
                        <IconButton
                          aria-label="add an alarm"
                          onClick={handleOpenDrawer(_index)}
                        >
                          <ErrorOutlineRoundedIcon className="icon-rejection" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={ROW_PER_PAGE}
              component="div"
              count={props.countClaims}
              rowsPerPage={pageSize}
              page={page}
              labelRowsPerPage={translate({ ...messages.linesPerPage }, props)}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </div>
        </CardContent>
      </Card>
      <RejectedReasonsDrawer
        open={isOpen}
        handleToggleDrawer={handleCloseDrawer}
        rejectionReasons={get(
          props.rejectedClaims,
          [`${index}`, `rejectionReasons`],
          [],
        )}
        otherRejectionReason={get(
          props.rejectedClaims,
          [`${index}`, `otherRejectionReason`],
          [],
        )}
        rejectionReasonsListing={get(props.listings, 'rejectionReasons', [])}
      />
    </Container>
  );
};

const mapDispatchToProps = {
  requestRejectedClaims,
};

const mapStateToProps = createStructuredSelector({
  rejectedClaims: makeSelectRejectedClaims(),
  countClaims: makeSelectCountRejectedClaims(),
  listings: makeSelectListings(),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(injectIntl(PreOpenManagerRejectedClaims));
